import { AfterViewInit, Component, input, OnInit, output, ViewChild } from '@angular/core';
import { COMMA, ENTER } from '@angular/cdk/keycodes';

import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { firstValueFrom, map, Observable, startWith, Subscription } from 'rxjs';
import { ComponentPortal } from '@angular/cdk/portal';

import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatIcon } from '@angular/material/icon';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSlideToggle } from '@angular/material/slide-toggle';

import { ImageCropComponent } from '../../../base/image/image-crop/image-crop.component';

import { ImageFormat } from '../../../../state-management/model/enums/screen-size.enums';
import { FileType } from '../../../../state-management/model/enums/state.enums';
import { SpecifiedImageGalleryImage } from '../base-image-gallery/base-image-gallery.component';

import { WebsiteStateService } from '../../../../state-management/services/website-state.service';
import { ImageUploadService } from '../../../../services/image-upload.service';

import { v4 as uuidv4 } from 'uuid';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { DEFAULT_IMAGE_GALLERY_FILENAME, DEFAULT_IMAGE_GALLERY_FOLDER, DEFAULT_ROOT_FOLDER, MSG_DISALLOW_DELETE_IMAGE_MIN_THRESHOLD, MIN_IMAGES_IN_GALLERY, MSG_DISALLOW_TOGGLE_THUMBNAILS_MIN_THRESHOLDS, MIN_IMAGES_IN_GALLERY_PREVIEW, MSG_DISALLOW_DELETE_IMAGE_MIN_THRESHOLD_OF_THUMBNAILS, MSG_CONFIRM_DELETE_IMAGE } from '../../../../state-management/constants/global.constants';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { GenericDialog, DialogData } from '../../../meta/website-config/website-config-form/website-config-form-page-administration/generic-dialog/generic-dialog.component';
import { environment } from '../../../../../environments/environment';
import { MatOption } from '@angular/material/core';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { LoggerService } from '../../../../services/logger.service';


@Component({
  selector: 'app-edit-image-gallery',
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatChipsModule,
    MatSlideToggle,
    MatOption,
    MatAutocompleteModule,
    MatAutocompleteTrigger,
    MatIcon,
    MatFormFieldModule,
    MatPaginator,
    MatSort,
    MatSortModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
  ],
  templateUrl: './edit-image-gallery.component.html',
  styleUrl: './edit-image-gallery.component.scss',
})
export class EditImageGalleryComponent implements OnInit, AfterViewInit {
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];
  readonly elemId = input.required<string>();
  readonly onClose = output<void>();

  private overlayRef: OverlayRef | null = null;
  private overlaySubscription: Subscription = new Subscription();
  private hasChangedTags: boolean = false;
  private hasChangedConfig: boolean = false;

  public displayedColumns = ['image', 'tags', 'showAsThumbnail', 'showInGallery', 'actions'];
  public imageGalleryFolder = this.stateService.getImageGalleryStoragePath();
  public imageGalleryFileName = DEFAULT_IMAGE_GALLERY_FILENAME;
  public supportedFileTypes = [FileType.WEBP, FileType.JPEG];

  public images = new MatTableDataSource<SpecifiedImageGalleryImage>([]);

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // Autocomplete-related properties
  public tagControl = new FormControl<string | null>(null);
  public filteredTags!: Observable<string[]>;

  ngAfterViewInit() {
    this.images.paginator = this.paginator;
    this.images.sort = this.sort;

    this.images.sortingDataAccessor = (item, property) => {
      const value = item[property as keyof SpecifiedImageGalleryImage];

      if (typeof value === 'boolean' || typeof value === 'number') {
        if (item.isPlaceholder) {
          if (this.sort.direction === 'desc') {
            return Number.POSITIVE_INFINITY;
          } else if (this.sort.direction === 'asc') {
            return Number.NEGATIVE_INFINITY;
          } else {
            return 0;
          }
        } else {
          return value ? 1 : 0; // Treat boolean as 1 or 0 for sorting
        }
      }

      if (Array.isArray(value)) {
        if (item.isPlaceholder) {
          if (this.sort.direction === 'desc') return 'ZZZZZ'
          if (this.sort.direction === 'asc') return 'AAAAA'
          return '';
        }
        return value.join(', '); // Join array values for sorting
      }
      else return ''; // Default case for other types
    }
  }

  applyTagFilter(value: Event | MatAutocompleteSelectedEvent) {
    let filterValue = ''
    if (value instanceof MatAutocompleteSelectedEvent) {
      filterValue = value.option.viewValue.trim().toLowerCase();
    }
    else if (value instanceof Event) {
      filterValue = (value.target as HTMLInputElement).value.trim().toLowerCase();
    }
    this.images.filterPredicate = (data, filter) => {
      if (data.isPlaceholder) {
        return true;
      }
      return data.tags.some((tag: string) => tag.toLowerCase().includes(filter));
    };
    this.images.filter = filterValue;
  }

  constructor(
    private logger: LoggerService,
    private overlay: Overlay,
    private dialog: MatDialog,
    private stateService: WebsiteStateService,
    private imageUploadService: ImageUploadService
  ) { }

  ngOnInit(): void {
    this.initImages();
    this.initFilteredTags();
  }

  private initFilteredTags() {
    this.filteredTags = this.tagControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const filterValue = (value || '').toLowerCase();
        return this.uniqueTags().filter(tag => tag.toLowerCase().includes(filterValue));
      })
    );
  }

  public uniqueTags(): string[] {
    const allTags = this.images.data.flatMap((image) => image.tags);
    const uniqueTags = Array.from(new Set(allTags)).sort((a, b) => a.localeCompare(b));
    return uniqueTags;
  }

  public addTag(image: SpecifiedImageGalleryImage, tag: string) {
    tag = tag.trim();
    if (tag) {
      image.tags.push(tag);
      image.newTag = '';
      this.hasChangedTags = true;
      this.initFilteredTags();
    }
  }

  public removeTag(image: SpecifiedImageGalleryImage, tag: string) {
    image.tags = image.tags.filter((t) => t !== tag);
    this.hasChangedTags = true;
    this.initFilteredTags();
  }

  public updateShowInGallery() {
    this.hasChangedConfig = true;
  }

  public updateShowAsThumbnail(image: SpecifiedImageGalleryImage) {

    if (this.images.data.filter((img) => img.showAsThumbnail).length < MIN_IMAGES_IN_GALLERY) {
      this.openGenericDialog(MSG_DISALLOW_TOGGLE_THUMBNAILS_MIN_THRESHOLDS).then(() => {
        this.images.data.forEach((img) => {
          if (img.uuid == image.uuid) {
            img.showAsThumbnail = true;
          }
        });
      });
      return;
    }

    if (image.showAsThumbnail) {
      this.images.data.forEach((img) => {
        if (img == image) {
          img.showInGallery = true;
        }
      });
    }

    this.hasChangedConfig = true;
  }

  public uploadImage(image: SpecifiedImageGalleryImage) {
    this.openPortal(image.uuid);
  }

  public async deleteImage(image: SpecifiedImageGalleryImage) {
    if (this.images.data.filter((img) => !img.isPlaceholder).length <= MIN_IMAGES_IN_GALLERY) {
      this.openGenericDialog(MSG_DISALLOW_DELETE_IMAGE_MIN_THRESHOLD);
      return;
    }

    if (image.showAsThumbnail && this.images.data.filter((img) => img.showAsThumbnail).length <= MIN_IMAGES_IN_GALLERY_PREVIEW) {
      this.openGenericDialog(MSG_DISALLOW_DELETE_IMAGE_MIN_THRESHOLD_OF_THUMBNAILS);
      return;
    }

    const confirmDelete = await this.openGenericDialog(MSG_CONFIRM_DELETE_IMAGE);
    if (confirmDelete) {
      this.images.data = this.images.data.filter((img) => img.uuid !== image.uuid);

      this.stateService.updateElement(this.elemId(), {
        config: {
          thumbnails: this.images.data.filter((img) => img.showAsThumbnail).map((img) => img.uuid),
          images: this.images.data.filter((img) => img.showInGallery).map((img) => img.uuid)
        }
      });
      this.hasChangedConfig = false;

      this.stateService.setAndSaveImageGallery(this.images.data).then(() => {
        this.hasChangedTags = false;
        const storageFolder = `/${environment.shopId}/${DEFAULT_ROOT_FOLDER}/${DEFAULT_IMAGE_GALLERY_FOLDER}/${image.uuid}`;
        this.imageUploadService.dropStorageFolder(storageFolder).then(() => {
          this.logger.log('Image deleted from storage');
        }).catch((err) => {
          this.logger.error('Error deleting image from storage:', err);
        });
      }).catch((err) => {
        this.logger.error('Error updating image gallery:', err);
      });
    } else {
      this.logger.log(`Löschen der Seite ${'page.name'} abgebrochen`);
    }
  }

  public confirm() {
    if (this.hasChangedConfig) {
      this.stateService.updateElement(this.elemId(), {
        config: {
          thumbnails: this.images.data.filter((img) => img.showAsThumbnail).map((img) => img.uuid),
          images: this.images.data.filter((img) => img.showInGallery).map((img) => img.uuid)
        }
      });
      this.hasChangedConfig = false;
    }
    // Then, update the image gallery in the state service - only needed in case tags have changed, upload and deletion are handled in the image cropper directly
    if (this.hasChangedTags) {
      this.stateService.setAndSaveImageGallery(this.images.data);
      this.hasChangedTags = false;
    }
    this.onClose.emit();
  }

  public cancel() {
    // this.initImages();
    this.onClose.emit();
  }

  public hasChanges() {
    return this.hasChangedTags || this.hasChangedConfig;
  }


  private initImages() {
    const blankImage: SpecifiedImageGalleryImage[] = [{ uuid: uuidv4(), timestamp: Date.now(), tags: [], showInGallery: false, showAsThumbnail: false, newTag: '', isPlaceholder: true }]
    const imageGallery = JSON.parse(JSON.stringify(this.stateService.getImageGalleryById(this.elemId())()));
    this.images.data = blankImage.concat(imageGallery);
  }


  // ### Overlay methods for dialog portal ###
  private openPortal(uuid: string) {
    this.overlayRef = this.overlay.create({
      hasBackdrop: true,
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      backdropClass: 'cdk-overlay-dark-backdrop', // Add a slightly darker backdrop
    });

    const portal = new ComponentPortal(ImageCropComponent);
    const componentRef = this.overlayRef.attach(portal);

    componentRef.instance.elemId = 'ImageGallery/' + uuid;
    componentRef.instance.requestedFormats = [ImageFormat.Free];
    componentRef.instance.fileTypes = this.supportedFileTypes;
    componentRef.instance.mode = 'image-gallery';
    componentRef.instance.maxWidth = 600;
    componentRef.instance.maxHeight = 600;

    this.overlaySubscription.add(
      componentRef.instance.imageSaved.subscribe(() => {
        this.images.data = this.images.data.map((img) => {
          if (img.uuid === uuid) {
            img.isPlaceholder = false;
          }
          return img;
        });

        this.stateService.setAndSaveImageGallery(this.images.data).then(() => {
          this.initImages();
        }).catch((err) => {
          this.logger.error('Error updating image gallery:', err);
        });
      })
    );

    this.overlaySubscription.add(
      componentRef.instance.closePortal.subscribe(() => {
        this.closeOverlay();
      })
    );

    this.overlaySubscription.add(
      this.overlayRef.backdropClick().subscribe(() => {
        this.closeOverlay();
      })
    );
  }

  private closeOverlay() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private openGenericDialog(data: DialogData): Promise<boolean> {
    const dialogRef = this.dialog.open(GenericDialog, {
      data: data,
      panelClass: "admin-theme"
    });
    return firstValueFrom(dialogRef.afterClosed());
  }
}